import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import {
  XMarkIcon,
  EnvelopeIcon,
  UserGroupIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PaperClipIcon,
  // TrashIcon, // pièces jointes ad hoc — désactivé pour le moment
} from '@heroicons/react/24/outline';
import { isAssembleeGenerale, getConvocationModalTitle } from '../utils/reunionTypes';

const FONCTION_LABELS = {
  president: 'Président',
  vice_president: 'Vice-président',
  vice_president_2: 'Vice-président 2',
  secretaire: 'Secrétaire',
  secretaire_adjoint: 'Secrétaire-adjoint',
  tresorier: 'Trésorier',
  tresorier_adjoint: 'Trésorier-adjoint',
  representant_cooperatives: 'Représentant coopératives',
  representant_syndicat: 'Représentant syndicat',
};

export default function ConvocationModal({
  isOpen,
  onClose,
  reunionId,
  reunionType,
  reunionOrganisme,
  reunionDate,
  reunionLieu,
  storedPiecesJointes = [],
  onSuccess,
}) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [preparingPdf, setPreparingPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [membres, setMembres] = useState([]);
  const [anneesDisponibles, setAnneesDisponibles] = useState([]);
  const [selectedAnnees, setSelectedAnnees] = useState([]);
  const [typeOrganisme, setTypeOrganisme] = useState('association');
  const [objet, setObjet] = useState('');
  const [contenu, setContenu] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [pdfInfo, setPdfInfo] = useState(null);
  // Pièces jointes ad hoc dans la popup — désactivé pour le moment
  // const [piecesJointes, setPiecesJointes] = useState([]);

  const isAG = isAssembleeGenerale(reunionType);

  useEffect(() => {
    if (isOpen && reunionId) {
      setStep(1);
      setObjet('');
      setContenu('');
      setResult(null);
      setError('');
      setPdfInfo(null);
      // setPiecesJointes([]);
      setMembres([]);
      setAnneesDisponibles([]);
      setSelectedAnnees([]);
      fetchMembres(isAG ? [] : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reunionId, reunionType, reunionDate]);

  const fetchMembres = async (annees = null) => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (isAG && annees && annees.length > 0) {
        params.set('annees', annees.join(','));
      }
      const url = `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/membres-convocation${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setMembres(response.data.membres || []);
      setTypeOrganisme(response.data.typeOrganisme);

      if (response.data.isAssembleeGenerale) {
        const available = response.data.anneesDisponibles || [];
        setAnneesDisponibles(available);
        if (annees === null) {
          const defaultAnnees = available.map((a) => a.annee);
          setSelectedAnnees(defaultAnnees);
          if (defaultAnnees.length > 0) {
            await fetchMembres(defaultAnnees);
            return;
          }
        } else {
          setSelectedAnnees(annees);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des destinataires');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAnnee = (annee) => {
    const next = selectedAnnees.includes(annee)
      ? selectedAnnees.filter((a) => a !== annee)
      : [...selectedAnnees, annee].sort((a, b) => b - a);
    setSelectedAnnees(next);
    fetchMembres(next);
  };

  const handlePreparerConvocation = async () => {
    setPreparingPdf(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const body = isAG ? { annees: selectedAnnees } : {};
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/preparer-convocation`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Pré-remplir l'objet et le contenu avec les données du backend
      setObjet(response.data.objetMail);
      setContenu(response.data.contenuMail.replace(/\n/g, '<br>'));
      setPdfInfo({
        url: response.data.pdfUrl,
        key: response.data.pdfKey,
        fileName: response.data.pdfFileName,
      });
      setMembres(response.data.destinataires.map((d, i) => ({ ...d, _id: i })));
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la préparation de la convocation');
    } finally {
      setPreparingPdf(false);
    }
  };

  /*
  const handlePiecesJointesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPiecesJointes((prev) => {
      const merged = [...prev];
      for (const file of files) {
        if (merged.length >= 5) break;
        if (!merged.some((f) => f.name === file.name && f.size === file.size)) {
          merged.push(file);
        }
      }
      return merged.slice(0, 5);
    });
    e.target.value = '';
  };

  const removePieceJointe = (index) => {
    setPiecesJointes((prev) => prev.filter((_, i) => i !== index));
  };
  */

  const handleSend = async () => {
    if (!objet.trim() || !contenu.trim()) {
      alert('L\'objet et le contenu sont requis');
      return;
    }

    setSending(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('objet', objet);
      formData.append('contenu', contenu);
      if (pdfInfo?.key) {
        formData.append('pdfKey', pdfInfo.key);
      }
      if (isAG && selectedAnnees.length > 0) {
        formData.append('annees', selectedAnnees.join(','));
      }
      // Pièces jointes ad hoc — désactivé pour le moment
      // piecesJointes.forEach((file) => {
      //   formData.append('piecesJointes', file);
      // });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionId}/convoquer`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setResult(response.data);
      // Mettre à jour la réunion dans le parent
      if (response.data.reunion && onSuccess) {
        onSuccess(response.data.reunion);
      }
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const getTitle = () => getConvocationModalTitle(reunionType, reunionOrganisme, typeOrganisme);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-indigo-600 px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <EnvelopeIcon className="h-5 w-5" />
              {getTitle()}
            </h3>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Steps indicator */}
          <div className="px-6 py-3 bg-gray-50 border-b">
            <div className="flex items-center justify-center gap-4">
              <div className={`flex items-center gap-2 ${step >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>1</span>
                <span className="text-sm font-medium">Destinataires</span>
              </div>
              <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              <div className={`flex items-center gap-2 ${step >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>2</span>
                <span className="text-sm font-medium">Rédaction</span>
              </div>
              <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              <div className={`flex items-center gap-2 ${step >= 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${step >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-300'}`}>3</span>
                <span className="text-sm font-medium">Confirmation</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4" style={{ minHeight: '400px' }}>
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Step 1: Liste des membres */}
            {step === 1 && (
              <div>
                {isAG && (
                  <div className="mb-5">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Années d'adhésion à inviter
                    </h4>
                    <p className="text-sm text-gray-500 mb-3">
                      Adhérents du {reunionOrganisme} avec une adhésion au statut actif ou expirée.
                    </p>
                    {anneesDisponibles.length === 0 ? (
                      <p className="text-sm text-honey-700 bg-honey-50 border border-honey-200 rounded-lg p-3">
                        Aucune adhésion active ou expirée trouvée pour ce groupement.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-3">
                        {anneesDisponibles.map(({ annee, count }) => (
                          <label
                            key={annee}
                            className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                              selectedAnnees.includes(annee)
                                ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedAnnees.includes(annee)}
                              onChange={() => handleToggleAnnee(annee)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-sm font-medium">
                              Adhérents {annee}
                            </span>
                            <span className="text-xs text-gray-500">({count})</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 mb-4">
                  <UserGroupIcon className="h-5 w-5 text-gray-400" />
                  <h4 className="font-medium text-gray-900">
                    Destinataires de la convocation ({membres.length})
                  </h4>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : membres.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {isAG
                      ? (selectedAnnees.length === 0
                        ? 'Sélectionnez au moins une année d\'adhésion'
                        : 'Aucun adhérent avec e-mail pour les années sélectionnées')
                      : 'Aucun membre trouvé'}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {membres.map((membre) => (
                      <div
                        key={membre._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {membre.prenom} {membre.nom}
                          </p>
                          <p className="text-sm text-gray-500">{membre.email}</p>
                        </div>
                        {membre.fonction && (
                          <span className="text-sm text-indigo-600 font-medium">
                            {FONCTION_LABELS[membre.fonction] || membre.fonction}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Rédaction du mail */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Objet du mail *
                  </label>
                  <input
                    type="text"
                    value={objet}
                    onChange={(e) => setObjet(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: Convocation - Assemblée Générale du 15 janvier 2025"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Corps du mail *
                  </label>
                  <Editor
                    apiKey="yz5jdxbkp4i9v4omefqdsmbc6pxaqmyor9p6sh5cfi2f3gz5"
                    value={contenu}
                    onEditorChange={(content) => setContenu(content)}
                    init={{
                      height: 250,
                      menubar: false,
                      plugins: ['lists', 'link', 'code'],
                      toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link',
                      content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
                      language: 'fr_FR',
                      entity_encoding: 'raw',
                      encoding: 'xml',
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pièces jointes
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Fichiers joints automatiquement à l&apos;e-mail de convocation.
                  </p>

                  <div className="space-y-2 rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {pdfInfo && (
                      <div className="flex items-center justify-between gap-3 p-3 bg-green-50/80">
                        <div className="flex items-center gap-2 min-w-0">
                          <DocumentTextIcon className="h-5 w-5 text-green-600 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-green-900 truncate">
                              {pdfInfo.fileName || 'Document de convocation'}
                            </p>
                            <p className="text-xs text-green-700">PDF de convocation</p>
                          </div>
                        </div>
                        <a
                          href={pdfInfo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-green-700 hover:text-green-900 shrink-0"
                        >
                          <DocumentArrowDownIcon className="h-4 w-4" />
                          Aperçu
                        </a>
                      </div>
                    )}

                    {storedPiecesJointes.map((pj) => (
                      <div
                        key={pj._id}
                        className="flex items-center gap-2 p-3 bg-honey-50/50"
                      >
                        <PaperClipIcon className="h-5 w-5 text-honey-600 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 truncate">{pj.nomOriginal}</p>
                          <p className="text-xs text-gray-500">Pièce jointe enregistrée sur la réunion</p>
                        </div>
                      </div>
                    ))}

                    {!pdfInfo && storedPiecesJointes.length === 0 && (
                      <p className="p-3 text-sm text-gray-400 italic">Aucune pièce jointe</p>
                    )}
                  </div>

                  {/*
                  Ajout de fichiers ponctuels dans la popup — désactivé pour le moment
                  <p className="text-xs text-gray-500 mt-3 mb-2">
                    Vous pouvez ajouter jusqu&apos;à 5 fichiers ponctuels
                    (PDF, Word, Excel, PowerPoint, images — 10 Mo max chacun).
                  </p>
                  <label className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                    <PaperClipIcon className="h-4 w-4 text-indigo-600" />
                    Ajouter des fichiers
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
                      onChange={handlePiecesJointesChange}
                      disabled={piecesJointes.length >= 5}
                      className="sr-only"
                    />
                  </label>
                  {piecesJointes.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {piecesJointes.map((file, index) => (
                        <li
                          key={`${file.name}-${file.size}-${index}`}
                          className="flex items-center justify-between gap-3 p-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <PaperClipIcon className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="truncate text-gray-800">{file.name}</span>
                            <span className="text-xs text-gray-500 shrink-0">
                              ({(file.size / 1024).toFixed(0)} Ko)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePieceJointe(index)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Retirer"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  */}
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && result && (
              <div className="text-center py-8">
                {result.emailsEchoues === 0 ? (
                  <>
                    <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      Convocation envoyée avec succès !
                    </h4>
                    <p className="text-gray-600">
                      {result.emailsEnvoyes} email{result.emailsEnvoyes > 1 ? 's' : ''} envoyé{result.emailsEnvoyes > 1 ? 's' : ''}
                    </p>
                  </>
                ) : (
                  <>
                    <ExclamationCircleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      Envoi partiellement réussi
                    </h4>
                    <p className="text-gray-600 mb-2">
                      {result.emailsEnvoyes} envoyé{result.emailsEnvoyes > 1 ? 's' : ''}, {result.emailsEchoues} échec{result.emailsEchoues > 1 ? 's' : ''}
                    </p>
                    {result.erreurs?.length > 0 && (
                      <div className="mt-4 text-left bg-red-50 p-3 rounded-lg text-sm">
                        <p className="font-medium text-red-700 mb-1">Erreurs :</p>
                        {result.erreurs.map((err, idx) => (
                          <p key={idx} className="text-red-600">{err.email}: {err.erreur}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between">
            {step === 1 && (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Annuler
                </button>
                <button
                  onClick={handlePreparerConvocation}
                  disabled={
                    membres.length === 0
                    || preparingPdf
                    || (isAG && selectedAnnees.length === 0)
                  }
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {preparingPdf ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Génération du PDF...
                    </>
                  ) : (
                    <>
                      Préparer la convocation
                      <ArrowRightIcon className="h-4 w-4" />
                    </>
                  )}
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Retour
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || !objet.trim() || !contenu.trim()}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="h-4 w-4" />
                      Envoyer
                    </>
                  )}
                </button>
              </>
            )}

            {step === 3 && (
              <button
                onClick={onClose}
                className="ml-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
